import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, Paper, Divider, TextField, Button, Stack, 
    Grid, Switch, FormControlLabel, IconButton, InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// --- Generador del estado inicial para el padre ---
export const generarEstadoInicialRed = () => ({
    wifi: { hab: 1, ssid: "ssid", pass: "123456" },
    gprs: { hab: 1, apn: "internet.tigo.bo" },
    http: { hab: 1, url: "alarmasCerberus.com", port: 80 },
    mqtt: {
        broker: "mosquitto.org", port: 1883, user: "admin", pass: "123456",
        topicDev: "ce/00010001", topicServer: "ce/server", topicBcast: "ce/bcast",
        qos: 0, kalive: 30
    }
});

function ConfiguracionRedForm({
    config, setConfig,
    state, setState,
    originalConfigRef,
}) {
    const { jsonOutput, formDirty, jsonNeedsUpdate } = state;
    const theme = useTheme();

    const [showWifiPassword, setShowWifiPassword] = useState(false);
    const [showMqttPassword, setShowMqttPassword] = useState(false);

    useEffect(() => {
        if (!jsonOutput) {
            setState(prev => ({
                ...prev,
                jsonOutput: JSON.stringify(config, null, 2),
                jsonNeedsUpdate: false
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const originalConfigString = JSON.stringify(originalConfigRef.current);
        const currentConfigString = JSON.stringify(config);
        const isDirty = currentConfigString !== originalConfigString;
        
        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '{}');
            needsUpdate = JSON.stringify(jsonObject) !== currentConfigString;
        } catch (e) { needsUpdate = true; }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({ ...prev, formDirty: isDirty, jsonNeedsUpdate: needsUpdate }));
        }
    }, [config, jsonOutput, formDirty, jsonNeedsUpdate, originalConfigRef, setState]);
    
    const handleChange = (group, field) => (event) => {
        const { value, type, checked } = event.target;
        const valorFinal = type === 'checkbox' ? (checked ? 1 : 0) : type === 'number' ? parseInt(value, 10) || 0 : value;
        
        setConfig(prev => ({
            ...prev,
            [group]: {
                ...prev[group],
                [field]: valorFinal
            }
        }));
    };

    const handleGenerateJson = () => {
        setState(prev => ({ ...prev, jsonOutput: JSON.stringify(config, null, 2), jsonNeedsUpdate: false }));
    };

    const sendConfig = () => {
        //console.log("--- Enviando Configuración de Red JSON ---", config);
        alert("Configuración de Red enviada. Revisa la consola.");
        originalConfigRef.current = JSON.parse(JSON.stringify(config));
        setState(prev => ({ ...prev, formDirty: false }));
    };

    const handleCancel = () => {
        if (window.confirm("¿Seguro que quieres descartar los cambios?")) {
            setConfig(originalConfigRef.current);
        }
    };
    
    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>Configuración de Red</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={(e) => { e.preventDefault(); sendConfig(); }}>
                    <Grid container spacing={4}>
                        {/* Columna izquierda: Formulario */}
                        <Grid item xs={12} md={6}>
                            <Stack spacing={3}>
                                {/* WiFi */}
                                <Paper variant="outlined" sx={{ p: 2, pt: 3, position: 'relative' }}>
                                    <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>WIFI</Typography>
                                    <FormControlLabel control={<Switch checked={!!config.wifi.hab} onChange={handleChange('wifi', 'hab')} />} label="Habilitar WiFi" />
                                    <TextField fullWidth size="small" sx={{mt: 1}} label="SSID" value={config.wifi.ssid} onChange={handleChange('wifi', 'ssid')} />
                                    <TextField fullWidth size="small" sx={{mt: 2}} label="Contraseña" type={showWifiPassword ? 'text' : 'password'} value={config.wifi.pass} onChange={handleChange('wifi', 'pass')}
                                        InputProps={{ endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowWifiPassword(!showWifiPassword)} edge="end">
                                                    {showWifiPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )}}
                                    />
                                </Paper>
                                
                                {/* GPRS */}
                                <Paper variant="outlined" sx={{ p: 2, pt: 3, position: 'relative' }}>
                                    <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>GPRS</Typography>
                                    <FormControlLabel control={<Switch checked={!!config.gprs.hab} onChange={handleChange('gprs', 'hab')} />} label="Habilitar GPRS" />
                                    <TextField fullWidth size="small" sx={{mt: 1}} label="APN" value={config.gprs.apn} onChange={handleChange('gprs', 'apn')} />
                                </Paper>

                                {/* HTTP */}
                                <Paper variant="outlined" sx={{ p: 2, pt: 3, position: 'relative' }}>
                                    <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>HTTP</Typography>
                                    <FormControlLabel control={<Switch checked={!!config.http.hab} onChange={handleChange('http', 'hab')} />} label="Habilitar HTTP" />
                                    <TextField fullWidth size="small" sx={{mt: 1}} label="URL" value={config.http.url} onChange={handleChange('http', 'url')} />
                                    <TextField fullWidth size="small" sx={{mt: 2}} label="Puerto" type="number" value={config.http.port} onChange={handleChange('http', 'port')} />
                                </Paper>
                                
                                 {/* MQTT */}
                                 <Paper variant="outlined" sx={{ p: 2, pt: 3, position: 'relative' }}>
                                    <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>MQTT</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Broker" value={config.mqtt.broker} onChange={handleChange('mqtt', 'broker')} /></Grid>
                                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Puerto" type="number" value={config.mqtt.port} onChange={handleChange('mqtt', 'port')} /></Grid>
                                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Usuario" value={config.mqtt.user} onChange={handleChange('mqtt', 'user')} /></Grid>
                                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Contraseña" type={showMqttPassword ? 'text' : 'password'} value={config.mqtt.pass} onChange={handleChange('mqtt', 'pass')}
                                            InputProps={{ endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowMqttPassword(!showMqttPassword)} edge="end">
                                                        {showMqttPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )}}
                                        /></Grid>
                                        <Grid item xs={12}><TextField fullWidth size="small" label="Topic Dispositivo" value={config.mqtt.topicDev} onChange={handleChange('mqtt', 'topicDev')} /></Grid>
                                        <Grid item xs={12}><TextField fullWidth size="small" label="Topic Servidor" value={config.mqtt.topicServer} onChange={handleChange('mqtt', 'topicServer')} /></Grid>
                                        <Grid item xs={12}><TextField fullWidth size="small" label="Topic Broadcast" value={config.mqtt.topicBcast} onChange={handleChange('mqtt', 'topicBcast')} /></Grid>
                                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="QoS" type="number" value={config.mqtt.qos} onChange={handleChange('mqtt', 'qos')} inputProps={{min:0, max:2}} /></Grid>
                                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="KeepAlive (seg)" type="number" value={config.mqtt.kalive} onChange={handleChange('mqtt', 'kalive')} /></Grid>
                                    </Grid>
                                </Paper>
                            </Stack>
                        </Grid>

                        {/* Columna derecha: JSON y Resumen */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>JSON DE CONFIGURACIÓN</Typography>
                                <Box sx={{ flexGrow: 1, overflowY: 'auto', border: `1px solid ${jsonNeedsUpdate ? theme.palette.warning.main : theme.palette.divider}`, borderRadius: 1, p: 1.5, backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'background.paper', minHeight: '200px' }}>
                                    <Typography component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', m: 0, fontSize: '0.875rem' }}>
                                        {jsonOutput}
                                    </Typography>
                                </Box>
                                <Button fullWidth variant="contained" color={jsonNeedsUpdate ? "warning" : "primary"} onClick={handleGenerateJson} sx={{ mt: 1 }}>
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>RESUMEN DE CONFIGURACIÓN</Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color={formDirty ? 'error' : 'inherit'}>**Formulario Modificado:** {formDirty ? 'Sí' : 'No'}</Typography>
                                    <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'inherit'}>**JSON Actualizado:** {jsonNeedsUpdate ? 'No' : 'Sí'}</Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        
                        {/* Botones de Acción */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={!formDirty}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={jsonNeedsUpdate}>Enviar</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracionRedForm;